import type { ElementType, ReactNode } from 'react'
import { cn } from '../../lib/utils'

interface EditableValueProps {
    /** False for a reader: plain text, no pointer, no button role. */
    canEdit: boolean
    onEdit: () => void
    /** Tooltip and accessible label of the clickable value ("Click to edit"). */
    title: string
    /** Muted copy shown when `children` is empty. */
    placeholder: string
    children?: ReactNode
    as?: ElementType
    className?: string
    'data-testid'?: string
}

/**
 * Resting state of an editable single value: it looks like text, shows a hover
 * background and a pointer cursor, and one click (or Enter / Space) opens the
 * editor. See the "Editable value" entry of the design reference.
 */
export function EditableValue({
    canEdit,
    onEdit,
    title,
    placeholder,
    children,
    as: Tag = 'span',
    className,
    'data-testid': testId,
}: EditableValueProps) {
    const empty = children === null || children === undefined || children === '' || children === false
    const content = empty ? <span className="text-muted-foreground">{canEdit ? placeholder : '—'}</span> : children
    if (!canEdit) {
        return <Tag className={className} data-testid={testId}>{content}</Tag>
    }
    return (
        <Tag
            className={cn('-mx-1 cursor-pointer rounded px-1 transition-colors hover:bg-muted/50', className)}
            role="button"
            tabIndex={0}
            title={title}
            onClick={onEdit}
            onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onEdit()
                }
            }}
            data-testid={testId}
        >
            {content}
        </Tag>
    )
}
